import * as React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '@proptr/api-client/react';
import { cn, initials } from '../../lib/utils.js';
import { Button } from '../ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.js';
import { Logo } from './Logo.js';
import { ThemeToggle } from './ThemeToggle.js';

const NAV: { to: string; label: string }[] = [
  { to: '/', label: 'Dashboard' },
  { to: '/agencies', label: 'Agencies' },
  { to: '/users', label: 'Users' },
  { to: '/partners', label: 'Partners' },
  { to: '/scrapers', label: 'Scrapers' },
  { to: '/cron', label: 'Cron handler' },
];

export function Topbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);

  async function onLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <>
      <header className="sticky top-0 z-30 border-b border-[hsl(var(--hairline))] bg-card">
        <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center gap-3 px-4 md:gap-6 md:px-6">
          {/* Mobile hamburger */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu />
          </Button>

          {/* Brand */}
          <div className="flex shrink-0 items-center">
            <Logo size="sm" />
          </div>

          {/* Desktop nav */}
          <nav className="hidden min-w-0 flex-1 items-center md:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'relative flex h-16 items-center px-4 font-sans text-[14px] font-medium transition-colors',
                    isActive
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span>{item.label}</span>
                    {isActive && (
                      <span className="absolute inset-x-4 bottom-0 h-[2px] rounded-t-full bg-primary" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <ThemeToggle />
            <div className="mx-1 hidden h-5 w-px bg-[hsl(var(--hairline))] md:block" />
            <ProfileMenu user={user} onLogout={onLogout} />
          </div>
        </div>
      </header>

      <MobileNavDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}

function ProfileMenu({
  user,
  onLogout,
}: {
  user: { name?: string; email?: string; role?: string } | null;
  onLogout: () => void | Promise<void>;
}) {
  const displayName = user?.name ?? user?.email?.split('@')[0] ?? 'User';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2.5 rounded-md py-1 pl-1 pr-2 outline-none ring-offset-background transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring md:pr-3"
          aria-label="Account menu"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted font-mono text-[11px] font-bold uppercase">
            {initials(user?.name ?? user?.email ?? 'U')}
          </div>
          <div className="hidden text-left md:block">
            <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.05em] leading-none">
              {displayName}
            </div>
            {user?.role && (
              <div className="mt-0.5 text-[10px] capitalize text-muted-foreground leading-none">
                {user.role}
              </div>
            )}
          </div>
          <ChevronDown className="hidden h-3.5 w-3.5 text-muted-foreground md:block" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel>
          <div className="font-semibold normal-case text-foreground">{displayName}</div>
          {user?.email && (
            <div className="text-[11px] font-normal normal-case text-muted-foreground">
              {user.email}
            </div>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            void onLogout();
          }}
        >
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileNavDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 md:hidden',
        open ? 'pointer-events-auto' : 'pointer-events-none',
      )}
      aria-hidden={!open}
    >
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-background/70 backdrop-blur-sm transition-opacity',
          open ? 'opacity-100' : 'opacity-0',
        )}
      />
      <aside
        className={cn(
          'absolute inset-y-0 left-0 flex w-[280px] flex-col border-r border-[hsl(var(--hairline))] bg-card shadow-pop transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-[hsl(var(--hairline))] px-4">
          <Logo size="sm" />
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close menu">
            <X />
          </Button>
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 p-3">
          <div className="eyebrow px-2 pb-2">Console</div>
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'relative flex items-center gap-2.5 rounded-md px-3 py-2.5 font-sans text-[14px] font-medium transition-colors',
                  isActive
                    ? 'bg-primary/10 text-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute inset-y-2 left-0 w-[2px] rounded-full bg-primary" />
                  )}
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>
    </div>
  );
}
